import { useMemo } from 'react';
import { useExperiments } from '../contexts/ExperimentContext';
import { trackEvent } from '../handlers/analytics';

// Persistent, cookieless anonymous id. Generated once and reused across visits
// so variant assignment is sticky for a given browser. Wrapped in try/catch so
// a locked-down localStorage never breaks the site.
const getAnonId = () => {
  try {
    let uid = window.localStorage.getItem('ep_uid');
    if (!uid) {
      uid =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem('ep_uid', uid);
    }
    return uid;
  } catch (e) {
    return '';
  }
};

// Small deterministic string hash (FNV-1a) normalized to a float in [0, 1).
// Same input always yields the same bucket, so `uid:experimentKey` maps a
// visitor to a stable point on the weight line.
const hashToUnit = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Coerce to unsigned 32-bit, then normalize.
  return (h >>> 0) / 0xffffffff;
};

// Pick a variant by cumulative weight. Weights need not sum to 1 — we scale the
// bucket by the total. Returns null if there are no usable variants.
const pickVariant = (variants, bucket) => {
  if (!Array.isArray(variants) || variants.length === 0) return null;
  const total = variants.reduce((sum, v) => sum + (Number(v.weight) || 0), 0);
  if (total <= 0) return variants[0].key || null;

  let threshold = bucket * total;
  for (let i = 0; i < variants.length; i += 1) {
    threshold -= Number(variants[i].weight) || 0;
    if (threshold < 0) return variants[i].key || null;
  }
  return variants[variants.length - 1].key || null;
};

// Fire an exposure event once per session per experiment. The results dashboard
// joins on these, so we guard with a sessionStorage flag to avoid double counts.
const trackExposureOnce = (key, variant) => {
  try {
    const flag = `exp:${key}`;
    if (window.sessionStorage.getItem(flag)) return;
    window.sessionStorage.setItem(flag, variant);
    trackEvent('experiment_exposure', { experiment: key, variant });
  } catch (e) {
    // Never let exposure tracking break render.
  }
};

// Returns the assigned variant key for `key`, or null if the experiment isn't
// active. Deterministic and sticky for a given browser.
export const useExperiment = (key) => {
  const { experiments } = useExperiments();

  return useMemo(() => {
    if (!key || !Array.isArray(experiments)) return null;

    const experiment = experiments.find((e) => e && e.key === key);
    if (!experiment) return null;

    const uid = getAnonId();
    const bucket = hashToUnit(`${uid}:${key}`);
    const variant = pickVariant(experiment.variants, bucket);
    if (!variant) return null;

    trackExposureOnce(key, variant);
    return variant;
  }, [experiments, key]);
};

export default useExperiment;
